import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { akvToResource, akvToModel, parameterizableSecretBaseToModel, parameterizablePasswordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { Constants } from 'app/shared/shared-constants';
import { isString } from 'app/shared/utils/string.util';
import { IStringMap } from 'app/interfaces/IStringMap';
import { getValue } from 'app/extensibility/utils/property-bag.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { ValueAdapter } from '../value.adapter';
import { AdapterContext, ITridentResource, snowflakeAuthTypes, SnowflakeLinkedService } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SnowflakeLinkedService),
    disabled: __IS_FABRIC_DPE__,
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: SnowflakeLinkedService) => {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                password: 'secret',
                authenticationType: '*',
                oauthTokenEndpoint: '*',
                clientId: '*',
                clientSecret: 'secret',
                oauthUserName: '*',
                oauthPassword: 'secret',
                scope: '*',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class SnowflakeLinkedServiceAdapter extends LinkedServiceAdapter<SnowflakeLinkedService> {
    protected supportFirstClassParameterExperience = true;
    protected modelConstructor(): new () => SnowflakeLinkedService {
        return SnowflakeLinkedService;
    }

    protected model(): IModel {
        return SnowflakeLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SnowflakeLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SnowflakeLinkedService;// try using `valueToResource()` from `linkedservice.adapter.ts` instead. These 2 functions are almost identical;
        properties.type = Metamodel.modelFor(SnowflakeLinkedService).name;
        let connectionString: string | Model.SecretBase;

        if (!linkedService.enableAKV) {
            const accountName = this.valueToResource(linkedService, SnowflakeLinkedService.ACCOUNT_NAME);
            const account = `jdbc:snowflake://${accountName || ''}.snowflakecomputing.com/?`;

            const propertiesBag: IStringMap<string | undefined> = {};
            propertiesBag['user'] = this.valueToResource(linkedService, SnowflakeLinkedService.USER_NAME);
            if (linkedService.authenticationType === snowflakeAuthTypes.Basic &&
                linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                propertiesBag['pwd'] = this.valueToResource(linkedService, SnowflakeLinkedService.PASSWORD);
            }
            propertiesBag['db'] = this.valueToResource(linkedService, SnowflakeLinkedService.DATABASE);
            propertiesBag['warehouse'] = this.valueToResource(linkedService, SnowflakeLinkedService.WAREHOUSE);
            propertiesBag['role'] = this.valueToResource(linkedService, SnowflakeLinkedService.ROLE);
            linkedService.additionalFields?.forEach((pair) => {
                if (pair.value != null) {
                    propertiesBag[pair.key] = pair.value;
                }
            });

            let connectionStringForGrouping = '';
            Object.keys(propertiesBag).forEach((key) => {
                const value = getValue(propertiesBag, key);
                if (!!value) {
                    connectionStringForGrouping += `${key}=${value}&`;
                }
            });
            connectionStringForGrouping = connectionStringForGrouping.substring(0, connectionStringForGrouping.length - 1);

            const connectionStringResource = linkedService.accountname === Constants.maskedCredential &&
                linkedService.database === Constants.maskedCredential &&
                linkedService.user === Constants.maskedCredential &&
                linkedService.password === Constants.maskedCredential &&
                linkedService.role === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.warehouse === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(account + connectionStringForGrouping);

            connectionString = connectionStringResource;

        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }

        if (linkedService.authenticationType === snowflakeAuthTypes.Basic) {
            properties.typeProperties = {
                authenticationType: linkedService.authenticationType,
                connectionString,
                password: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
            };
        } else if (linkedService.authenticationType === snowflakeAuthTypes.OAuth) {
            properties.typeProperties = {
                connectionString,
                authenticationType: linkedService.authenticationType,
                oauthTokenEndpoint: this.valueToResource(linkedService, SnowflakeLinkedService.OAUTH_TOKEN_ENDPOINT),
                clientId: this.valueToResource(linkedService, SnowflakeLinkedService.CLIENT_ID),
                clientSecret: parameterizablePasswordOrAKVtoSecretBase(linkedService, SnowflakeLinkedService.CLIENT_SECRET, this.context),
                oauthUserName: this.valueToResource(linkedService, SnowflakeLinkedService.OAUTH_USER_NAME),
                oauthPassword: parameterizablePasswordOrAKVtoSecretBase(linkedService, SnowflakeLinkedService.OAUTH_PASSWORD, this.context),
                scope: this.valueToResource(linkedService, SnowflakeLinkedService.SCOPE)
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SnowflakeLinkedService): Promise<SnowflakeLinkedService> {
        const properties = linkedServiceResource.properties as Model.SnowflakeLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.SnowflakeLinkedServiceTypeProperties = properties.typeProperties;
        const connectionString = typeProperties.connectionString;
        linkedService.accountname = undefined;
        linkedService.user = undefined;
        linkedService.password = undefined;
        linkedService.warehouse = undefined;
        linkedService.database = undefined;
        linkedService.additionalFields = [];

        linkedService.authenticationType = typeProperties.authenticationType || snowflakeAuthTypes.Basic;

        if (linkedService.authenticationType === snowflakeAuthTypes.OAuth) {
            linkedService.isExistingResourceUsingOAuth = true;
            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.CLIENT_ID, typeProperties.clientId);
            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.OAUTH_TOKEN_ENDPOINT, typeProperties.oauthTokenEndpoint);
            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.OAUTH_USER_NAME, typeProperties.oauthUserName);
            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.SCOPE, typeProperties.scope);
            parameterizableSecretBaseToModel(linkedService, SnowflakeLinkedService.CLIENT_SECRET, typeProperties.clientSecret, this.context);
            parameterizableSecretBaseToModel(linkedService, SnowflakeLinkedService.OAUTH_PASSWORD, typeProperties.oauthPassword, this.context);
        }

        if (isString(connectionString)) {
            const accountRegex = /^jdbc\:snowflake\:\/\/(.*)\.snowflakecomputing\.com\/\?(.*)/g;
            const accountNameMatchResult = accountRegex.exec(connectionString);
            const connectionStringForSplitting = accountNameMatchResult[2];
            const keyValPairs = connectionStringForSplitting.split('&');
            if (accountNameMatchResult && accountNameMatchResult.length === 3) {
                ValueAdapter.toModel(linkedService, SnowflakeLinkedService.ACCOUNT_NAME, accountNameMatchResult[1]);
                keyValPairs.forEach((item) => {
                    // sync up with be owner, key doesn't contain '='
                    const sepIndex = item.indexOf('=');
                    if (sepIndex >= 0) {
                        const key = item.substr(0, sepIndex);
                        const val = item.substr(sepIndex + 1);
                        if (key === 'user') {
                            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.USER_NAME, val);
                        } else if (key === 'db') {
                            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.DATABASE, val);
                        } else if (key === 'warehouse') {
                            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.WAREHOUSE, val);
                        } else if (key === 'role') {
                            ValueAdapter.toModel(linkedService, SnowflakeLinkedService.ROLE, val);
                        } else if ('pwd' !== key) {
                            linkedService.additionalFields.push({ key, value: val });
                        }
                    }
                });

                linkedService.password = Constants.maskedCredential;
                if (linkedService.authenticationType === snowflakeAuthTypes.Basic && typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, SnowflakeLinkedService.PASSWORD, typeProperties.password, this.context);
                }
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.accountname = Constants.maskedCredential;
            linkedService.user = Constants.maskedCredential;
            linkedService.password = Constants.maskedCredential;
            linkedService.warehouse = Constants.maskedCredential;
            linkedService.database = Constants.maskedCredential;
            if (linkedService.authenticationType === snowflakeAuthTypes.Basic && properties.typeProperties.password) {
                parameterizableSecretBaseToModel(linkedService, SnowflakeLinkedService.PASSWORD, properties.typeProperties.password, this.context);
            }
        } else if ((connectionString as Model.SecretBase)?.type === 'AzureKeyVaultSecret') {
            linkedService.referedAKV = akvToModel(connectionString as Model.AzureKeyVaultSecretReference, this.context);
            linkedService.enableAKV = true;
        }

        return linkedService;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource) => {
        try {
            const { extensionDataSourcePath } = JSON.parse(tridentResource.connectionDetails);
            const [accountName = '', warehouse = ''] = extensionDataSourcePath.split(';'); // Fabric warehouse cannot contain ';'
            if (!accountName && !warehouse) {
                return undefined;
            }
            return {
                connectionString: `jdbc:snowflake://${accountName}.snowflakecomputing.com/?warehouse=${warehouse}`
            };
        } catch (error) {
            return undefined;
        }
    };
}
